/**
 * 신규 센터 온보딩 브리핑 — 판정.
 *
 * ─────────────────────────────────────────────────────────────────
 * 왜 별개인가
 * ─────────────────────────────────────────────────────────────────
 * 기존 브리핑은 **수강권을 가진 회원**을 전제로 만들어졌다.
 * 그래서 신규 센터에서는 아무것도 나오지 않는다.
 *
 *   지엘핏쌍문본점 실측(2026-08-26): 수강 회원 6명 · 수강권 6명 전원 미등록
 *   → 브리핑 「오늘은 급하게 챙길 일이 없습니다」 · 회원 현황 「아직 관리할 회원이 없습니다」
 *
 * 서버도 이 영역을 명시적으로 뺐다 (`opsPace.service.ts`):
 *   「수업 기록이 아예 없으면 아직 시작을 안 한 것이라 여기서 다루지 않습니다
 *     — 그건 이탈이 아니라 온보딩 문제이고, 대응이 완전히 다릅니다.」
 *
 * 맞는 판단이었다. 이탈 관리와 온보딩은 대응이 다르다.
 * 다만 「다루지 않는다」로 끝나서 신규 센터가 브리핑을 못 쓰고 있다.
 *
 * ─────────────────────────────────────────────────────────────────
 * 🔴 데이터는 회원 현황과 **분리해서** 받는다 (서버 확인 Q14, 2026-08-26)
 * ─────────────────────────────────────────────────────────────────
 * `/members` 는 `memberType:'original'`(앱 연동) 만 남기는 필터가 걸려 있다.
 * 앱 미연동 회원은 **행 자체가 없다.**
 *
 * 이 필터를 푸는 것은 additive 가 아니라 **파괴적 변경**이다 —
 * 회원 현황의 모든 지표·판정·단계 분류가 앱 연동 회원을 전제로 돌아가서,
 * 수기 회원이 행으로 섞이면 이탈 판정 모집단과 단계 인원·금액 합계가 오염된다.
 *
 * 그래서 온보딩은 **자기 집계**로 받는다.
 * 회원 현황 = 앱 연동 회원의 운영 화면 (현행 유지)
 * 온보딩     = 별도 집계로 만드는 새 카테고리
 *
 * ─────────────────────────────────────────────────────────────────
 * 설계 원칙 — 기존 브리핑 기획서를 그대로 따른다
 * ─────────────────────────────────────────────────────────────────
 * ① 🔴 **0 과 null 은 다르다** (§5-②)
 *      0    = 재봤더니 0이다        → 「완료」
 *      null = 아직 못 잰다           → 「확인 중」, **완료로 세지 않는다**
 *    null 을 0 으로 다루면 「다 끝났습니다」가 되어 원장이 온보딩을 멈춘다.
 *
 * ② **한 번에 하나만 보여준다** (§4-4 「원장님은 아침에 30초를 씁니다」)
 *    강사가 0명인데 「수업일지를 쓰세요」는 할 수 없는 말이다.
 *
 * ③ **근거 없는 판단은 내보내지 않는다** (§5-①)
 *    각 단계가 왜 미완료인지 수치를 함께 준다.
 */

/* ═══════════════════════════════════════════════════════════════════
 * 계약 — 서버가 보내 줄 형태
 * ═══════════════════════════════════════════════════════════════════ */

/**
 * 온보딩 집계.
 *
 * 🔴 **모르는 값은 `null`.** `0` 으로 주지 마세요.
 * 예: 예약 집계 경로가 아직 없으면 `scheduledMembers: null` 입니다.
 *     0 으로 주면 「아무도 예약을 안 잡았다」가 되어 원장이 헛수고를 합니다.
 */
export type OnboardingPayload = {
    /** 센터 개설일 (KST 'YYYY-MM-DD'). `center.createdAt` */
    openedOn: string | null;

    /** 등록된 강사 수 */
    coachCount: number;
    /** 그중 강사 앱 연동을 수락한 수. `center.relations[].isConnected === true` */
    coachConnectedCount: number | null;
    /** 담당 회원이 한 명도 없는 강사 이름들 */
    coachesWithoutMembers: string[] | null;

    /** 센터에 등록된 회원 수 (앱 미연동 포함 — `/members` 와 모수가 다릅니다) */
    memberCount: number;
    /** 그중 앱 미연동. `memberType !== 'original'` */
    memberAppUnlinkedCount: number | null;
    /** 그중 활성 수강권이 없는 회원 수 */
    memberWithoutMembershipCount: number | null;
    /** 수강권은 있는데 예약이 한 건도 없는 회원 수 */
    memberWithoutBookingCount: number | null;

    /**
     * 강사 관리 습관 — 각각 비율(%)입니다.
     * 대상이 없으면 `null` (0% 로 주면 「아무도 안 한다」가 됩니다)
     */
    journalPct: number | null;
    mealLogPct: number | null;
    conditionCheckPct: number | null;

    /**
     * 🔴 이 센터에서 **켜지 않은** 기능들 (2026-08-26).
     *
     * 식단은 일부 센터에서만 켭니다. 안 쓰는 센터에 「식단 확인 중」이 뜨면
     * 원장은 **집계를 기다립니다** — 영원히 오지 않을 숫자를요.
     * 「미적용」과 「아직 못 잼」은 완전히 다른 말입니다.
     */
    disabledFeatures?: Array<'meal' | 'condition'>;

    /**
     * 각 항목의 대상 명단 (선택).
     * 있으면 화면이 「누구에게」까지 보여 줍니다.
     */
    samples?: {
        appUnlinked?: string[];
        withoutMembership?: string[];
        withoutBooking?: string[];
    };
};

/* ═══════════════════════════════════════════════════════════════════
 * 판정
 * ═══════════════════════════════════════════════════════════════════ */

export type OnboardingStepKey =
    | 'ADD_COACH'
    | 'CONNECT_COACH'
    | 'ASSIGN_MEMBER'
    | 'INVITE_APP'
    | 'ADD_MEMBERSHIP'
    | 'ADD_BOOKING'
    | 'COACH_HABIT';

/**
 * 못 잰 것과 안 한 것을 화면에서 구분하기 위한 상태.
 *
 * `REFERENCE` 는 **문턱이 없는 항목**이다 (대표 결정 2026-08-26, D6).
 * 숫자만 보여주고 완료/미완료를 판정하지 않는다 —
 * 라포 전체 평균이 일지 48.4% 라 50% 를 문턱으로 두면
 * **평균적인 센터가 전부 「미완료」로 남는다.**
 */
export type StepStatus = 'DONE' | 'TODO' | 'UNKNOWN' | 'REFERENCE';

export type OnboardingStep = {
    key: OnboardingStepKey;
    /** 화면 라벨 */
    title: string;
    status: StepStatus;
    /**
     * 남은 대상 수. `null` 이면 **못 잰 것**이다 — 0 과 다르다.
     * 화면은 `null` 일 때 숫자를 그리지 않고 「확인 중」으로 표시한다.
     */
    remaining: number | null;
    /** 「왜 이 단계인가」 — 근거 수치를 문장으로 */
    detail: string;
    /** 대상 이름 (있을 때만) */
    names: string[];
    /**
     * 🔴 온보딩 **종료 판정**에 넣는 단계인가 = `CORE_STEPS` 이면서 잴 수 있는 것.
     *
     * 못 재는 항목을 완료로 세면 「온보딩 끝났습니다」가 거짓말이 되고,
     * 반대로 분모에 남겨 두면 **영원히 온보딩 화면에 갇힌다.**
     */
    counted: boolean;
};

/**
 * 🔴 온보딩을 접는 기준이 되는 단계 — 대표 결정 2026-08-26 (D7).
 *
 * 「강사 등록 → 강사 연동 → 회원 배정 → 수강권 등록 → 첫 수업 예약」
 *
 * 이 다섯이면 **분석에 필요한 데이터가 전부 생기기 시작한다.**
 * 수강권이 있어야 잔여·만료가 생기고, 예약이 잡혀야 출석·페이스가 쌓인다.
 *
 * 회원 앱 설치와 관리 습관은 **여기 넣지 않는다.**
 * 끝나는 일이 아니라 계속 관리하는 일이라, 종료 조건에 넣으면
 * 센터가 온보딩 화면을 못 벗어난다. 대신 브리핑 안에서 계속 챙긴다 —
 * 회원 앱 연동은 강사 정착 점수에도 들어간다 (`coachSettling.ts`, D8).
 */
export const CORE_STEPS: OnboardingStepKey[] = [
    'ADD_COACH',
    'CONNECT_COACH',
    'ASSIGN_MEMBER',
    'ADD_MEMBERSHIP',
    'ADD_BOOKING',
];

const step = (
    key: OnboardingStepKey,
    title: string,
    remaining: number | null,
    detail: string,
    opts: { names?: string[]; reference?: boolean } = {}
): OnboardingStep => ({
    key,
    title,
    // 🔴 null 은 DONE 이 아니다. 못 잰 것을 완료로 만들면 온보딩이 멈춘다.
    status: opts.reference
        ? 'REFERENCE'
        : remaining == null
          ? 'UNKNOWN'
          : remaining > 0
            ? 'TODO'
            : 'DONE',
    remaining,
    detail,
    names: opts.names ?? [],
    /*
     * 🔴 **핵심 단계이면서 잴 수 있는 것만 종료 판정에 넣는다.**
     *
     * `remaining != null` 을 빼먹었다가 실제 버그가 났다 —
     * 「첫 수업 예약」이 집계 경로가 없어 `remaining: null` 인데 분모에 남아서
     *   ① 진행도가 「3 / 6」으로 나왔다 (잴 수 있는 건 5개인데)
     *   ② `finished` 가 영원히 false 라 **온보딩 카드에서 못 빠져나갔다**
     * 못 재는 것을 기다리면 센터가 온보딩 화면에 갇힌다.
     */
    counted: CORE_STEPS.includes(key) && remaining != null,
});

const pct = (v: number | null, off = false): string =>
    off ? '미적용' : v == null ? '확인 중' : `${Math.round(v)}%`;

/**
 * 🔴 「아직 못 잼」과 「앞으로도 안 잼」은 다릅니다 (백엔드 3차에서 확정).
 *
 * `mealLogPct`·`conditionCheckPct` 는 서버가 **항상 null** 로 보냅니다.
 * 집계 경로가 아예 없어서입니다 —
 *   · 식단: `Diet` 모델에 centerId·coachId 가 없어 센터 단위로 셀 수가 없습니다.
 *   · 컨디션: 셀 수는 있지만 그건 **회원이 앱에서 입력하는 값**입니다.
 *     그 비율을 「강사 관리 습관」 칸에 넣으면 원장이 회원이 안 한 일로 강사를 부릅니다.
 *
 * 이걸 「확인 중」으로 적으면 원장은 **영원히 오지 않을 숫자를 기다립니다.**
 * (이 파일 위쪽 `disabledFeatures` 주석이 경고하던 바로 그 상황입니다 —
 *  거기는 「센터가 안 켰다」이고 여기는 「라포가 못 잰다」로, 원인은 다르지만
 *  원장이 기다리게 된다는 결과는 같습니다)
 *
 * 나중에 서버가 재기 시작하면 숫자가 그대로 찍힙니다 — 이 함수를 안 고쳐도 됩니다.
 */
export const UNMEASURED_TEXT = '측정 안 함';

const pctOrUnmeasured = (v: number | null, off = false): string =>
    off ? '미적용' : v == null ? UNMEASURED_TEXT : `${Math.round(v)}%`;

/**
 * 온보딩 단계 목록.
 *
 * 순서가 곧 우선순위다. 앞이 안 되면 뒤는 할 수가 없다 —
 * 강사가 0명인데 「회원을 배정하세요」는 할 수 없는 말이다.
 */
export const resolveOnboardingSteps = (p: OnboardingPayload): OnboardingStep[] => {
    const s = p.samples ?? {};

    /* ① 강사 등록 — 여기서 막히면 아무것도 시작이 안 된다 */
    const addCoach = step(
        'ADD_COACH',
        '강사 등록',
        p.coachCount > 0 ? 0 : 1,
        p.coachCount > 0 ? `강사 ${p.coachCount}명` : '아직 등록된 강사가 없습니다'
    );

    /* ② 강사 앱 연동 — 관리자가 연결하면 false 로 시작하고 강사가 수락하면 true */
    const notConnected =
        p.coachConnectedCount == null ? null : Math.max(0, p.coachCount - p.coachConnectedCount);
    /*
     * 🔴 이름을 「강사 앱 연동」에서 바꿨다 (개발자 지적, 2026-08-26).
     *
     * `isConnected` 가 재는 것은 **센터 관계 수락**이고,
     * 앱 설치·로그인은 `Auth.deviceToken` · `Coach.isTutorial` 로 재는 **다른 사실**이다.
     * 로그인은 했는데 수락 전 / 수락은 했는데 토큰 없음이 둘 다 실존한다.
     *
     * 「앱 연동」이라 부르면 원장이 강사에게 「앱 깔았냐」고 묻는데
     * 강사는 이미 깔았고 수락만 안 한 상태다. 조치가 어긋난다.
     * 두 사실을 다 추적하는 것은 강사 정착 카드(`coachSettling.ts`)가 한다.
     */
    const connectCoach = step(
        'CONNECT_COACH',
        '센터 연결 수락',
        notConnected,
        notConnected == null
            ? '연동 상태를 확인 중입니다'
            : notConnected > 0
              ? `${notConnected}명이 아직 앱에서 수락하지 않았습니다`
              : `강사 ${p.coachCount}명 모두 연동`
    );

    /* ③ 회원 배정 */
    const without = p.coachesWithoutMembers;
    const assign = step(
        'ASSIGN_MEMBER',
        '회원 배정',
        without == null ? null : without.length,
        without == null
            ? '배정 현황을 확인 중입니다'
            : without.length > 0
              ? `${without.length}명의 강사에게 담당 회원이 없습니다`
              : '모든 강사에게 담당 회원이 있습니다',
        { names: without ?? [] }
    );

    /* ④ 회원 앱 설치 */
    const invite = step(
        'INVITE_APP',
        '회원 앱 설치',
        p.memberAppUnlinkedCount,
        p.memberAppUnlinkedCount == null
            ? '앱 연동 현황을 확인 중입니다'
            : p.memberAppUnlinkedCount > 0
              ? `${p.memberAppUnlinkedCount}명이 아직 앱을 쓰지 않습니다`
              : `회원 ${p.memberCount}명 모두 앱 연동`,
        { names: s.appUnlinked ?? [] }
    );

    /* ⑤ 수강권 등록 */
    const membership = step(
        'ADD_MEMBERSHIP',
        '수강권 등록',
        p.memberWithoutMembershipCount,
        p.memberWithoutMembershipCount == null
            ? '수강권 현황을 확인 중입니다'
            : p.memberWithoutMembershipCount > 0
              ? `${p.memberWithoutMembershipCount}명에게 수강권이 없습니다`
              : '모든 회원에게 수강권이 있습니다',
        { names: s.withoutMembership ?? [] }
    );

    /* ⑥ 첫 수업 예약 */
    const booking = step(
        'ADD_BOOKING',
        '첫 수업 예약',
        p.memberWithoutBookingCount,
        p.memberWithoutBookingCount == null
            ? '예약 현황을 확인 중입니다'
            : p.memberWithoutBookingCount > 0
              ? `${p.memberWithoutBookingCount}명이 아직 수업을 잡지 않았습니다`
              : '모든 회원이 수업을 잡았습니다',
        { names: s.withoutBooking ?? [] }
    );

    /* ⑦ 관리 습관 — 일지·식단·컨디션.
     *
     * 🔴 **문턱을 두지 않는다** (대표 결정 2026-08-26, D6).
     *
     * 앞서는 50% 아래를 「습관이 안 잡혔다」로 봤는데,
     * 라포 전체 평균이 **일지 48.4%** 다. 평균적인 센터가 전부 미달이 된다.
     * 그리고 습관은 **끝나는 일이 아니다** — 온보딩을 졸업해도 계속 관리한다.
     *
     * 그래서 숫자만 보여주고 판정하지 않는다.
     * 「잘 하고 있나」는 D+7 에 열리는 라포 벤치마크 신호가 답한다
     * (`briefingPhase.ts` · `COACH_BENCHMARK`) — 그쪽이 비교 대상까지 준다. */
    const off = p.disabledFeatures ?? [];
    const mealOff = off.includes('meal');
    const condOff = off.includes('condition');
    const habits = [p.journalPct, mealOff ? null : p.mealLogPct, condOff ? null : p.conditionCheckPct];
    const measured = habits.filter((v): v is number => v != null);
    const habit = step(
        'COACH_HABIT',
        '관리 습관',
        null,
        measured.length === 0 && p.journalPct == null
            ? '집계를 확인 중입니다'
            : `수업일지 ${pct(p.journalPct)} · 식단 ${pctOrUnmeasured(p.mealLogPct, mealOff)} · 컨디션 ${pctOrUnmeasured(p.conditionCheckPct, condOff)}`,
        { reference: true }
    );

    return [addCoach, connectCoach, assign, invite, membership, booking, habit];
};

/* ═══════════════════════════════════════════════════════════════════
 * 화면이 쓸 요약
 * ═══════════════════════════════════════════════════════════════════ */

export type OnboardingState = {
    steps: OnboardingStep[];
    /** 오늘 해야 할 한 가지. 전부 끝났으면 null */
    current: OnboardingStep | null;
    /** 완료 판정에 들어가는 단계 중 끝난 수 */
    doneCount: number;
    /** 완료 판정에 들어가는 단계 수 */
    countedTotal: number;
    /**
     * 🔴 온보딩을 접고 일반 브리핑으로 넘겨도 되는가.
     *
     * **핵심 5단계(`CORE_STEPS`)가 끝나면** 넘긴다 —
     * 강사 등록 · 강사 연동 · 회원 배정 · 수강권 등록 · 첫 수업 예약.
     * 여기까지면 분석에 필요한 데이터가 생기기 시작한다.
     *
     * 회원 앱 설치와 관리 습관은 기다리지 않는다.
     * 끝나는 일이 아니라 계속 관리하는 일이고,
     * 종료 조건에 넣으면 센터가 온보딩 화면을 못 벗어난다.
     * 대신 넘어간 뒤에도 브리핑이 계속 챙긴다.
     */
    finished: boolean;
    /**
     * 🔴 졸업했지만 아직 남은 것 — 「자연스럽게 넘어가도록」의 실체.
     *
     * 회원 앱 설치가 안 끝난 채로 일반 브리핑에 갔으면
     * 그 사실이 사라지면 안 된다. 여기 담아 화면이 이어서 챙긴다.
     */
    carryOver: OnboardingStep[];
    /** 개설 후 며칠째인가. 개설일을 모르면 null */
    dayCount: number | null;
};

export const resolveOnboarding = (
    p: OnboardingPayload,
    today: Date = new Date()
): OnboardingState => {
    const steps = resolveOnboardingSteps(p);
    const counted = steps.filter((x) => x.counted);
    const doneCount = counted.filter((x) => x.status === 'DONE').length;

    /* 오늘 할 일 = 가장 앞선 미완료.
     * 🔴 'UNKNOWN'(못 잼)은 오늘 할 일로 올리지 않는다 —
     *    원장이 할 수 있는 게 없는데 시키는 꼴이 된다.
     * 🔴 'REFERENCE'(관리 습관)도 올리지 않는다 — 문턱이 없으니 「끝내라」가 성립 안 한다. */
    const current = steps.find((x) => x.status === 'TODO') ?? null;

    /* 핵심이 끝난 뒤에도 남아 있는 것 — 일반 브리핑에서 이어 챙긴다 */
    const carryOver = steps.filter(
        (x) => !CORE_STEPS.includes(x.key) && x.status === 'TODO'
    );

    let dayCount: number | null = null;
    if (p.openedOn) {
        const opened = new Date(`${p.openedOn}T00:00:00+09:00`);
        const ms = today.getTime() - opened.getTime();
        if (Number.isFinite(ms)) dayCount = Math.max(1, Math.floor(ms / 86400000) + 1);
    }

    return {
        steps,
        current,
        doneCount,
        countedTotal: counted.length,
        // 🔴 핵심 5단계만 본다. 회원 앱 설치가 남아 있어도 졸업한다.
        finished: counted.length > 0 && doneCount === counted.length,
        carryOver,
        dayCount,
    };
};
